import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ProjectImageUpload, { ProjectImage } from './ProjectImageUpload';
import {ProjectDTO} from "../../../DTO/ProjectDTO";
import {ProjectStatus} from "../../../DTO/ProjectStatus";

interface Technology {
    id: number;
    name: string;
}

interface Props {
    project: ProjectDTO;
    onUpdated: (proj: ProjectDTO) => void;
    onClose: () => void;
}

const ProjectEditForm: React.FC<Props> = ({ project, onUpdated, onClose }) => {
    const [name, setName] = useState(project.name);
    const [slug, setSlug] = useState(project.slug);
    const [description, setDescription] = useState(project.description);
    const [readmeContent, setReadmeContent] = useState(project.readmeContent || '');
    const [startDate, setStartDate] = useState(project.startDate || '');
    const [endDate, setEndDate] = useState(project.endDate || '');
    const [status, setStatus] = useState<ProjectDTO['status']>(project.status);
    const [visible, setVisible] = useState(project.visible);
    const [projectUrl, setProjectUrl] = useState(project.projectUrl || '');
    const [repoUrl, setRepoUrl] = useState(project.repoUrl || '');
    const [technologyIds, setTechnologyIds] = useState<number[]>(project.technologies.map(t => t.id));
    const [allTechs, setAllTechs] = useState<Technology[]>([]);
    const [loading, setLoading] = useState(false);
    const [thumbFile, setThumbFile] = useState<File | null>(null);
    const [thumbLoading, setThumbLoading] = useState(false);
    const [gallery, setGallery] = useState<ProjectImage[]>(project.gallery || []);
    const readmeRef = useRef<HTMLTextAreaElement>(null);

    const insertImageMarkdown = (markdown: string) => {
        const el = readmeRef.current;
        if (!el) {
            setReadmeContent(prev => prev + '\n' + markdown);
            return;
        }
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = readmeContent.substring(0, start) + markdown + readmeContent.substring(end);
        setReadmeContent(next);
        requestAnimationFrame(() => {
            el.selectionStart = start + markdown.length;
            el.selectionEnd = start + markdown.length;
            el.focus();
        });
    };

    useEffect(() => {
        axios
            .get<Technology[]>('/api/technologies')
            .then((r) => setAllTechs(r.data))
            .catch(console.error);
    }, []);

    const handleUpdate = async () => {
        setLoading(true);
        try {
            const payload = {
                name,
                slug,
                description,
                readmeContent,
                startDate,
                endDate,
                status,
                visible,
                projectUrl,
                repoUrl,
                technologyIds,
            };
            const { data } = await axios.put<ProjectDTO>(`/api/projects/${project.id}`, payload);
            onUpdated(data);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleThumbnailUpload = async () => {
        if (!thumbFile) return;
        setThumbLoading(true);
        try {
            const form = new FormData();
            form.append('file', thumbFile);
            await axios.post(`/api/projects/${project.id}/thumbnail`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        } finally {
            setThumbLoading(false);
            setThumbFile(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-ink/50 flex items-start justify-center pt-20 z-50">
            <div className="bg-surface text-on-surface p-6 rounded shadow-md w-full max-w-2xl border border-border overflow-y-auto max-h-[90vh]">
                <h2 className="text-lg font-semibold mb-4">Edit Project</h2>

                {/* Thumbnail */}
                <div className="mb-6">
                    <h3 className="mb-2 font-medium">Thumbnail</h3>
                    {project.thumbnailUrl && (
                        <img
                            src={
                                process.env.REACT_APP_BACKEND_SERVER_URL! +
                                encodeURI(project.thumbnailUrl)
                            }
                            alt="Thumbnail preview"
                            className="w-32 h-32 object-cover mb-2 rounded border border-border"
                        />
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setThumbFile(e.target.files?.[0] || null)}
                        className="mb-2 block text-on-surface"
                    />
                    <button
                        onClick={handleThumbnailUpload}
                        disabled={thumbLoading || !thumbFile}
                        className="btn-secondary"
                    >
                        {thumbLoading ? 'Uploading…' : 'Upload Thumbnail'}
                    </button>
                </div>

                {/* Basic fields */}
                <input
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                />
                <input
                    type="text"
                    placeholder="Slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                />

                {/* Description & README */}
                <textarea
                    placeholder="Short description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface h-20"
                />
                <textarea
                    ref={readmeRef}
                    placeholder="README (Markdown)"
                    value={readmeContent}
                    onChange={(e) => setReadmeContent(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface h-32"
                />

                {/* Dates */}
                <div className="flex space-x-4 mb-3">
                    <div>
                        <label className="block mb-1">Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="border border-border rounded px-2 py-1 bg-background text-on-surface"
                        />
                    </div>
                    <div>
                        <label className="block mb-1">End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="border border-border rounded px-2 py-1 bg-background text-on-surface"
                        />
                    </div>
                </div>

                {/* Status & visibility */}
                <div className="flex items-center space-x-6 mb-3">
                    <div>
                        <label className="block mb-1">Status</label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                            className="border border-border rounded px-2 py-1 bg-background text-on-surface"
                        >
                            <option value="PLANNED">Planned</option>
                            <option value="IN_PROGRESS">In Progress</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="ON_HOLD">On Hold</option>
                            <option value="CANCELLED">Cancelled</option>
                        </select>
                    </div>
                    <label className="inline-flex items-center">
                        <input
                            type="checkbox"
                            checked={visible}
                            onChange={(e) => setVisible(e.target.checked)}
                            className="mr-2"
                        />
                        Visible
                    </label>
                </div>

                {/* Links */}
                <input
                    type="text"
                    placeholder="Live URL (optional)"
                    value={projectUrl}
                    onChange={(e) => setProjectUrl(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                />
                <input
                    type="text"
                    placeholder="Repository URL (optional)"
                    value={repoUrl}
                    onChange={(e) => setRepoUrl(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                />

                {/* Technologies */}
                <label className="block mb-1">Technologies</label>
                <select
                    multiple
                    value={technologyIds.map(String)}
                    onChange={(e) =>
                        setTechnologyIds(Array.from(e.target.selectedOptions, (o) => +o.value))
                    }
                    className="block mb-4 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                >
                    {allTechs.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>

                {/* Gallery */}
                <div className="mb-6">
                    <h3 className="mb-2 font-medium">Image Gallery</h3>
                    <ProjectImageUpload
                        projectId={project.id}
                        images={gallery || []}
                        onChange={setGallery}
                        onInsertMarkdown={insertImageMarkdown}
                    />
                </div>

                <div className="flex justify-end space-x-2 mt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded border border-border text-on-surface hover:bg-surface-hover"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleUpdate}
                        disabled={loading}
                        className="btn-primary"
                    >
                        {loading ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProjectEditForm;
